"use client";

import { ArrowUpRight, Blocks, BrainCircuit, Lock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function ContributionsEquipment() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "PRECISION OPTICS",
    icon: Blocks,
    description: "Our observatory utilizes diffraction-limited apochromatic refractors and high-resolution solar filters. Every photon is captured through multi-coated glass designed to minimize chromatic aberration, ensuring that the delicate structures of the solar chromosphere are rendered with absolute clarity."
  }, {
    number: "02",
    title: "DATA INTEGRITY",
    icon: BrainCircuit,
    description: "Scientific observation requires rigorous methodology. We employ cooled CMOS sensors and specialized tracking mounts to eliminate thermal noise and periodic error. Every frame in our logbook is a calibrated record of celestial phenomena, processed to preserve authentic astronomical detail."
  }, {
    number: "03",
    title: "REMOTE ACCESS",
    icon: Lock,
    description: "The observatory operates via a secure, low-latency network interface. Automated dome synchronization and weather-sensing safety protocols ensure the equipment is protected while providing real-time telemetry. A fortified digital gateway for deep sky exploration from any location."
  }];
  return;
}
function PillarCard({
  pillar,
  Icon,
  index
}: {
  pillar: {
    number: string;
    title: string;
    description: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 100}ms`
  }} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <Card className="group relative h-full min-h-[480px] bg-card backdrop-blur-xl border border-border rounded-lg p-10 md:p-12 flex flex-col shadow-lg hover:border-primary hover:shadow-2xl transition-all duration-500">
        {}
        <div className="font-mono text-5xl md:text-6xl font-light text-primary transition-transform duration-500 group-hover:scale-110 origin-left">
          {pillar.number}
        </div>

        {}
        <div className="h-px w-full bg-primary/20 mt-6 mb-10 group-hover:bg-primary transition-colors duration-500" />

        {}
        <Icon className="size-8 text-muted-foreground group-hover:text-primary transition-colors duration-500 mb-8" strokeWidth={1} />

        {}
        <h3 className="font-default font-bold text-2xl md:text-3xl uppercase tracking-tight mb-6 leading-tight">
          {pillar.title}
        </h3>

        {}
        <p className="font-serif text-base md:text-[17px] leading-relaxed text-muted-foreground mb-10 flex-grow">
          {pillar.description}
        </p>

        {}
        <div className="mt-auto">
          <Button variant="ghost" size="sm" className="font-mono text-xs tracking-widest uppercase text-foreground hover:text-primary hover:bg-transparent p-0 h-auto group/btn">
            TECHNICAL SPECS
            <ArrowUpRight className="size-4 text-primary transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
          </Button>
        </div>
      </Card>
    </div>;
}